import { tree } from "d3";
import { DomEventsSubscribe } from "../../events/dom-events-subscribe";
import { StringHelpers } from "../../helpers/string-helpers";
import { TradeOrderMessageHelpers } from "../../helpers/trade-order-message-helpers";
import { IChartContainer } from "../../models/base/chart-container.interface";
import { ChartSettingsPopupModel } from "../../models/chart-style/chart-settings-popup.model";
import { ChartStyleSettingsModel } from "../../models/chart-style/chart-style-settings.model";
import { QuoteType } from "../../models/trade/trade.model";
import { IComponent } from "../component.interface";
import { InformationPopupComponent, InformationPopupType } from "../information-popup.component";
import { TradeComponent } from "../trade.component";
import { TradeOrderTypeComponent } from "./trade-order-type.component";

export class BuyButtonWithTradeQuoteComponent implements IComponent {
    public id = '';

    private element: HTMLElement;
    private base: IChartContainer;
    private domEvents: DomEventsSubscribe;
    private plotLineActive = false;
    private subscribedToTradeComponent = false;

    constructor(el: HTMLElement, base: IChartContainer) {
        this.id = StringHelpers.newGuid();
        this.element = el;
        this.base = base;
        this.domEvents = base.domEvents;
        this.subscribeChartData();
        this.onMouseMove();
        this.onMouseDown();
        this.onMouseEnter();
        this.onMouseLeave();
        this.onClick();
        this.onEscPress();
        this.onContextMenuOpen();
        this.subscribeChartRequirmentData();
    }

    private subscribeChartData() {
        this.base.chartService.subscribeChartStyleData((data: ChartStyleSettingsModel) => {
            if (!data.chartSettingsPopupModel?.displayBrowserChartExistingBuySellButtons) {
                this.element.classList.add('hidden');
            } else {
                this.element.classList.remove('hidden');
            }
        });
    }

    private onMouseMove() {
        this.domEvents.onMouseMove(this.id, (event: MouseEvent) => {
            if (!this.base.tiChartSeries || !this.base.tiChartSeries.highStockChart || !this.plotLineActive) {
                return;
            }

            const yAxis = this.base.tiChartSeries.highStockChart.yAxis[0];
            if (!yAxis) {
                return;
            }

            const pointer = this.base.tiChartSeries.highStockChart.pointer.normalize(event);
            const parentElementRect = this.base.tiChartSeries.chartElement.getBoundingClientRect();
            const yTop = pointer.y - parentElementRect.y;

            const yValue = yAxis.toValue(yTop);
            yAxis.removePlotLine('buy-plot-line-trade-quote');
            yAxis.addPlotLine({
                value: yValue,
                id: 'buy-plot-line-trade-quote',
                className: 'buy-plot-line',
                zIndex: 10
            });
        });
    }

    private onMouseDown() {
        this.domEvents.onMouseDown(this.id, (event: MouseEvent) => {
            if (!this.base.tiChartSeries) {
                return;
            }

            if (!this.base.tiChartSeries.highStockChart || !this.plotLineActive || event.which == 3) {
                if (event.which == 3) {
                    this.inActivePlotLine();
                }

                return;
            }

            const yAxis = this.base.tiChartSeries.highStockChart.yAxis[0];
            if (!yAxis) {
                return;
            }
            
            const pointer = this.base.tiChartSeries.highStockChart.pointer.normalize(event);
            const parentElementRect = this.base.tiChartSeries.chartElement.getBoundingClientRect();
            const yTop = pointer.y - parentElementRect.y;

            const yValue = yAxis.toValue(yTop);

            const tradeComponent = this.base.getComponentsByType(TradeComponent)[0] as TradeComponent;
            const tradeModel = tradeComponent?.getTradeModel();

            const tradeOrderTypeComponent = this.base.getComponentsByType(TradeOrderTypeComponent)[0] as TradeOrderTypeComponent;
            if (tradeModel && tradeOrderTypeComponent.value && tradeOrderTypeComponent && !isNaN(tradeModel.quoteQuantity)) {
                const popup = this.base.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
                
                let chartSettingsPopupModel = this.base.tiChartSeries?.chartStyleSettings?.chartSettingsPopupModel;
                if (!chartSettingsPopupModel) {
                    chartSettingsPopupModel = new ChartSettingsPopupModel();
                }

                if (popup && chartSettingsPopupModel.displayOrderConfirmationPopup) {
                    const message = TradeOrderMessageHelpers.getMessage(
                        true,
                        tradeOrderTypeComponent.value as QuoteType,
                        tradeModel.orderType,
                        tradeModel.quoteQuantity,
                        tradeModel.tif,
                        this.base.tiChartSeries.symbol,
                        tradeModel.price,
                        tradeModel.account);
    
                    popup.show(message.title, message.message, (dontShowMessage: boolean) => {
                        chartSettingsPopupModel.displayOrderConfirmationPopup = !dontShowMessage;
                        this.base.chartService.requestSaveChartSettingsPopupModel(chartSettingsPopupModel);
                        this.base.chartService.requestInitiateChartTrade(yValue, true, tradeOrderTypeComponent.value, tradeComponent.getAccountName(), tradeComponent.getQuoteQuantity());
                    }, InformationPopupType.TradeConfirmation);
                } else {
                    this.base.chartService.requestInitiateChartTrade(yValue, true, tradeOrderTypeComponent.value, tradeComponent.getAccountName(), tradeComponent.getQuoteQuantity());
                }
            }
            
            this.inActivePlotLine();
        });
    }

    private onMouseEnter() {
        this.element.addEventListener('mouseenter', () => {
            this.base.chartService.requestBuySellButtonsHover(true, false);
        });
    }

    private onMouseLeave() {
        this.element.addEventListener('mouseleave', () => {
            this.base.chartService.requestBuySellButtonsHover(false, false);
        });
    }

    private validate(): boolean {
        const tradeOrderTypeComponent = this.base.getComponentsByType(TradeOrderTypeComponent)[0] as TradeOrderTypeComponent;
        if (!tradeOrderTypeComponent || !tradeOrderTypeComponent.getNumber()) {
            return false;
        }

        return true;
    }

    private onClick() {
        this.element.addEventListener('click', () => {
            if (!this.validate()) {
                const popup = this.base.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
                popup.show("Error", "Please set a Quantity.", null, InformationPopupType.Normal, () => {
                    const tradeOrderTypeComponent = this.base.getComponentsByType(TradeOrderTypeComponent)[0] as TradeOrderTypeComponent;
                    tradeOrderTypeComponent?.focusInput();
                });
                
                return;
            }

            if (this.element.containClass('disabled')) {
                return;
            }
            this.addPlotLine();
        });
    }

    private onEscPress() {
        this.domEvents.onEscPress(this.id, () => {
            this.inActivePlotLine();
        });
    }

    private onContextMenuOpen() {
        this.domEvents.onContextMenu(this.id, () => {
            this.inActivePlotLine();
        });
    }

    private inActivePlotLine() {
        if (!this.base.tiChartSeries) {
            return;
        }
        if (this.plotLineActive) {
            const yAxis = this.base.tiChartSeries.highStockChart.yAxis[0];
            yAxis?.removePlotLine('buy-plot-line-trade-quote');
            this.plotLineActive = false;
        }
    }

    private addPlotLine() {
        if (!this.base.tiChartSeries) {
            return;
        }
        const yAxis = this.base.tiChartSeries.highStockChart.yAxis[0];
        if (!yAxis) {
            return;
        }

        if (this.plotLineActive) {
            yAxis.removePlotLine('buy-plot-line-trade-quote');
        } else {
            this.plotLineActive = true;
            yAxis.addPlotLine({
                value: 0,
                id: 'buy-plot-line-trade-quote',
                className: 'buy-plot-line',
                zIndex: 10
            });
        }
    }

    private subscribeChartRequirmentData(tries?: number) {
        tries = tries ?? 1;
        
        if (tries >= 100) return;

        if (!this.subscribedToTradeComponent) {
            const tradeComponent = this.base.getComponentsByType(TradeComponent)[0] as TradeComponent;
            if (tradeComponent && tradeComponent.getAccountName()) {
                this.subscribedToTradeComponent = true;
                tradeComponent.onChange(() => {
                    this.toggleButton(tradeComponent.getAccountName());
                });
                this.toggleButton(tradeComponent.getAccountName());
            } else {
                setTimeout(() => {
                    this.subscribeChartRequirmentData(tries + 1);
                }, 1000);
            }
        }
    }

    private toggleButton(accountName: string) {
        if (!accountName) return;
        this.element.removeClass('disabled');
        this.element.setAttribute('title', 'Buy with ' + accountName);
    }
}